import { useState, useEffect, useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { CalendarRange, LogOut, Play, Scale } from "lucide-react";
import { Badge, Button, Panel, RuleResult, Skeleton, TextField, cx } from "@/sentinel";

export const RUN_LABELS = { 2: "Run 2 · pre-open", 3: "Run 3 · official open", 4: "Run 4 · +15m", 5: "Run 5 · +30m" };
const EXIT_LABELS = { profit_target: "Profit target", stop_loss: "Stop loss", time_exit: "Time exit", settlement: "Held to expiry" };

// ── shared research-table helpers (also used by pages/Backtest.jsx) ──────────

export const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));

/** Below this many trades a result is illustrative only: no headline figure, just the table and a note. */
export const MIN_N = 30;
export const smallSample = (n) => !isNum(n) || Number(n) < MIN_N;

/** A 95% interval from whichever shape the API sends: lower_ci95/upper_ci95, lower_ci/upper_ci or ci95: [lo, hi]. */
export function ci95(r) {
  if (!r) return null;
  const lo = r.lower_ci95 ?? r.lower_ci ?? r.ci95_low ?? (Array.isArray(r.ci95) ? r.ci95[0] : undefined);
  const hi = r.upper_ci95 ?? r.upper_ci ?? r.ci95_high ?? (Array.isArray(r.ci95) ? r.ci95[1] : undefined);
  return isNum(lo) || isNum(hi) ? [isNum(lo) ? Number(lo) : null, isNum(hi) ? Number(hi) : null] : null;
}
/** Lower 95% bound on the mean, from an explicit interval if the API sends one, else from sd and n. Null when neither is available. */
export function lowerBound95(r) {
  const ci = ci95(r);
  if (ci && isNum(ci[0])) return ci[0];
  const sd = r?.sd ?? r?.stdev ?? r?.std ?? r?.pnl_sd;
  if (isNum(sd) && isNum(r?.n) && Number(r.n) > 1 && isNum(r?.expectancy)) {
    return Number(r.expectancy) - 1.96 * Number(sd) / Math.sqrt(Number(r.n));
  }
  return null;
}
/** The slippage haircut is entered in index points per spread; the dollar figure is x100 (0.10 = $10). */
export const haircutUsd = (h) => usd((isNum(h) ? Number(h) : 0.10) * 100, 0);
export function CiText({ r }) {
  const ci = ci95(r);
  if (!ci) return <span className="sn-faint">—</span>;
  return <span className="sn-num">[{usd(ci[0], 2, { signed: true })}, {usd(ci[1], 2, { signed: true })}]</span>;
}

/** The "sample too small" note shown in place of a headline figure. */
export function SmallSampleNote({ n, what = "this result", ...rest }) {
  return (
    <p className="sn-row sn-small" style={{ margin: 0, gap: "var(--space-2)", color: "var(--text-primary)" }} {...rest}>
      <RuleResult result="UNCONFIRMED">n = {isNum(n) ? n : 0} &lt; {MIN_N}</RuleResult>
      Sample too small to judge {what}: figures are illustrative only and no headline is shown until at least {MIN_N} trades.
    </p>
  );
}

/** USD with thousands separators and a true minus. 2dp for per-trade figures, 0dp for totals. */
export function usd(v, dp = 2, { signed = false } = {}) {
  if (!isNum(v)) return "—";
  const n = Number(v);
  const s = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return (n < 0 ? "−" : signed && n > 0 ? "+" : "") + "$" + s;
}
/** 0.634 → "63%" */
export const pct0 = (v) => (isNum(v) ? `${Math.round(Number(v) * 100)}%` : "—");

/** A signed backtest delta: direction colour (blue ▲ / orange ▼) as text only, never a fill. */
export function SignedText({ value, children, ...rest }) {
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  return (
    <span {...rest} className={cx("sn-delta", "sn-price-" + dir)}>
      <span className="sn-arrow" aria-hidden="true">{dir === "up" ? "▲" : dir === "down" ? "▼" : "◆"}</span>{children}
    </span>
  );
}
/** Signed USD P&L / expectancy. */
export function Pnl({ value, dp = 2 }) {
  if (!isNum(value)) return <span className="sn-faint">—</span>;
  return <SignedText value={Number(value)}>{usd(value, dp, { signed: true })}</SignedText>;
}
/** Walk-forward stability as a validation result: ≥ 70% PASS, ≥ 50% UNCONFIRMED, below FAIL. */
export function Stability({ score, pos, total, ...rest }) {
  const s = score ?? 0;
  return (
    <RuleResult result={s >= 0.7 ? "PASS" : s >= 0.5 ? "UNCONFIRMED" : "FAIL"} {...rest}>
      {Math.round(s * 100)}% · {pos}/{total}
    </RuleResult>
  );
}

/** Entry checkpoint picker. A native select, so keyboard and test tooling behave as before. */
export function RunSelect({ value, onChange, testId, label = "Entry checkpoint" }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap">
        <select id={id} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-label={label ? undefined : "Entry checkpoint"}
                className="sn-input" style={{ cursor: "pointer" }} data-testid={testId}>
          {[2, 3, 4, 5].map((r) => <option key={r} value={r}>{RUN_LABELS[r]}</option>)}
        </select>
      </div>
    </div>
  );
}

export function NumInput({ label, value, onChange, step = "0.05", width = 112, testId, type = "number" }) {
  return (
    <div style={{ width }}>
      <TextField label={label} type={type} step={type === "number" ? step : undefined} numeric={type === "number"} value={value}
                 onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

/** A small titled table section inside a panel. */
function SubTable({ title, testId, children }) {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)", minWidth: 0 }} data-testid={testId}>
      {title && <span className="sn-micro">{title}</span>}
      <div className="sn-table-wrap">{children}</div>
    </div>
  );
}

// -------------------------------------------------------------- Exit management
export function ExitManagementCard({ start, end, haircut, commission, realFills }) {
  const [run, setRun] = useState(3);
  const [pt, setPt] = useState(0.5);
  const [sl, setSl] = useState(1.0);
  const [timeExit, setTimeExit] = useState("15:30");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [fillDelta, setFillDelta] = useState(null);

  useEffect(() => {
    api.btExitsCached(realFills).then((c) => { setData(c); if (c?.entry_run) setRun(c.entry_run); }).catch(() => {});
  }, [realFills]);

  // Auto-load exit fill-delta if both variants exist in cache.
  useEffect(() => {
    api.btExitsFillDelta().then(setFillDelta).catch(() => setFillDelta(null));
  }, [data]);

  const runExits = async () => {
    setBusy(true);
    try {
      const policies = [
        { label: `Target ${Math.round(pt * 100)}%`, pt_frac: Number(pt) },
        { label: `Stop ${Math.round(sl * 100)}%`, sl_frac: Number(sl) },
        { label: `Exit ${timeExit}`, time_exit: timeExit },
        { label: `Target ${Math.round(pt * 100)}% + Stop ${Math.round(sl * 100)}%`, pt_frac: Number(pt), sl_frac: Number(sl) },
        { label: "Full managed", pt_frac: Number(pt), sl_frac: Number(sl), time_exit: timeExit },
      ];
      const r = await api.btExits({ start, end, entry_run: run, haircut: Number(haircut), commission: Number(commission), policies, real_fills: !!realFills });
      setData(r);
      toast.success(`Compared ${r.scenarios.length} exit policies over ${start} → ${end}${realFills ? " (real NBBO fills)" : ""}.`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const best = data?.scenarios?.[0];
  const baseline = data?.scenarios?.find((s) => s.label === "Hold to expiry");
  const lift = best && baseline ? (best.expectancy - baseline.expectancy) : null;
  const bestSmall = best && smallSample(best.n);
  // "Best" is only claimed for a positive mean; a negative top row is "least bad", never best.
  const bestPositive = isNum(best?.expectancy) && Number(best.expectancy) > 0;

  return (
    <Panel title="Exit management · managed exits vs hold to expiry" icon={LogOut} data-testid="bt-exit-mgmt">
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          Reconstructs the spread&apos;s intraday liquidation value from stored minute bars and applies profit-target, stop-loss and time exits,
          then ranks each policy against holding to settlement. Exit policy is the largest single lever on realized 0DTE edge.
        </p>
        <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
          <RunSelect value={run} onChange={setRun} testId="bt-exit-run" />
          <NumInput label="Profit target (× max profit)" value={pt} onChange={setPt} width={176} testId="bt-exit-pt" />
          <NumInput label="Stop loss (× max loss)" value={sl} onChange={setSl} width={160} testId="bt-exit-sl" />
          <NumInput label="Time exit (ET)" type="time" value={timeExit} onChange={setTimeExit} width={128} testId="bt-exit-time" />
          <Button variant="primary" icon={Play} loading={busy} onClick={runExits} data-testid="bt-exit-run-btn">
            {busy ? "Running…" : "Run exit comparison"}
          </Button>
        </div>

        {bestSmall && <SmallSampleNote n={best.n} what="which exit policy is best" data-testid="bt-exit-small" />}
        {lift != null && !bestSmall && (
          <p className="sn-small" style={{ margin: 0, padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)" }}
             data-testid="bt-exit-lift">
            {bestPositive ? (
              <>Best policy <b style={{ color: "var(--text-primary)" }}>{best.label}</b> at{" "}</>
            ) : (
              <>No positive policy: every exit policy loses money per trade. Least bad is <b style={{ color: "var(--text-primary)" }}>{best.label}</b> at{" "}</>
            )}
            <span className="sn-num" style={{ color: "var(--text-primary)" }}>{usd(best.expectancy)}/trade</span>,{" "}
            <SignedText value={lift}>{usd(lift, 2, { signed: true })}/trade</SignedText> vs hold to expiry.
          </p>
        )}

        {data?.scenarios?.length > 0 && (
          <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="bt-exit-table">
            {data.computed_at && <span className="sn-small sn-faint"><span className="sn-num">{data.start} → {data.end}</span> · {RUN_LABELS[data.entry_run]}</span>}
            <div className="sn-table-wrap">
              <table className="sn-table" aria-label="Exit policies ranked by expectancy">
                <thead><tr><th>Exit policy</th><th className="r">Trades</th><th className="r">Hit rate</th><th className="r">Expectancy</th><th className="r">Total P&amp;L</th><th className="r">PF</th></tr></thead>
                <tbody>
                  {data.scenarios.map((s, i) => (
                    <tr key={s.label} data-testid={`bt-exit-row-${i}`}>
                      <td style={{ color: "var(--text-primary)" }}>
                        <span className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "nowrap" }}>{s.label}{i === 0 && !smallSample(s.n) && bestPositive && <Badge>Best</Badge>}</span>
                      </td>
                      <td className="num">{s.n}</td>
                      <td className="num">{s.n ? pct0(s.hit_rate) : "—"}</td>
                      <td className="num">{s.n ? <Pnl value={s.expectancy} /> : "—"}</td>
                      <td className="num">{s.n ? <Pnl value={s.total_pnl} dp={0} /> : "—"}</td>
                      <td className="num">{s.profit_factor ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {best?.by_exit && (
              <span className="sn-small sn-faint">
                Best policy exit mix:{" "}
                <span className="sn-sans-num">{Object.entries(best.by_exit).map(([k, v]) => `${EXIT_LABELS[k] || k} ${v.n} (${usd(v.expectancy)})`).join(" · ")}</span>
              </span>
            )}
          </div>
        )}

        {fillDelta?.by_policy?.length > 0 && (
          <div className="sn-stack" style={{ paddingTop: "var(--space-4)", borderTop: "1px solid var(--border-hairline)" }} data-testid="bt-exit-fill-delta">
            <div className="sn-row" style={{ gap: "var(--space-2)" }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Real fill vs modeled · slippage per exit policy</h3>
              <span className="sn-small sn-faint"><span className="sn-num">{fillDelta.start} → {fillDelta.end}</span> · {RUN_LABELS[fillDelta.entry_run]}</span>
            </div>
            <p className="sn-small sn-muted" style={{ margin: 0 }}>
              How much the real bid/ask spread costs each exit policy against the modeled <span className="sn-num">{haircutUsd(haircut)}</span> haircut ({isNum(haircut) ? Number(haircut) : 0.10} index points per spread), per trade. A larger figure means that policy&apos;s real exits give up more edge.
            </p>
            <div className="sn-table-wrap">
              <table className="sn-table" aria-label="Real fill vs modeled per exit policy">
                <thead><tr><th>Exit policy</th><th className="r">Trades</th><th className="r">Modeled exp.</th><th className="r">Real exp.</th><th className="r">Slippage / trade</th><th className="r">Real fills</th></tr></thead>
                <tbody>
                  {fillDelta.by_policy.map((r, i) => (
                    <tr key={r.label} data-testid={`bt-exit-fill-delta-row-${i}`}>
                      <td style={{ color: "var(--text-primary)" }}>{r.label}</td>
                      <td className="num">{r.n ?? "—"}</td>
                      <td className="num"><Pnl value={r.modeled_exp} /></td>
                      <td className="num"><Pnl value={r.real_exp} /></td>
                      <td className="num">{r.slippage != null ? (r.slippage >= 0 ? "−" + usd(r.slippage) : "+" + usd(Math.abs(r.slippage))) : "—"}</td>
                      <td className="num" title={r.median_fill_lag_s != null ? `median lag ${r.median_fill_lag_s}s` : ""}>{r.real_fill_pct != null ? pct0(r.real_fill_pct) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// -------------------------------------------------------------- Regime explorer
function RegimeTable({ title, groups, testId }) {
  const keys = Object.keys(groups || {});
  if (!keys.length) return null;
  return (
    <SubTable title={title} testId={testId}>
      <table className="sn-table" aria-label={title}>
        <thead><tr><th>Regime</th><th className="r">n</th><th className="r">Hit</th><th className="r">Exp.</th><th className="r">Total</th><th>Verdict</th></tr></thead>
        <tbody>
          {keys.map((k) => {
            const g = groups[k];
            const small = smallSample(g.n);
            // TRADE needs n >= MIN_N, the backend's positive-mean verdict AND a lower 95% bound above zero.
            // Without an interval from the API the slice is "Mean > 0, not yet confirmed", never TRADE.
            const lo = lowerBound95(g);
            let verdict;
            if (small) {
              verdict = g.recommend === "TRADE" ? <RuleResult result="UNCONFIRMED">n &lt; {MIN_N}</RuleResult> : <RuleResult result="NA">{g.recommend}</RuleResult>;
            } else if (g.recommend !== "TRADE") {
              verdict = <RuleResult result="NA">{g.recommend}</RuleResult>;
            } else if (lo != null && lo > 0) {
              verdict = <RuleResult result="PASS">TRADE</RuleResult>;
            } else {
              verdict = <RuleResult result="UNCONFIRMED">{lo != null ? "Mean > 0, not confirmed" : "Mean > 0, not yet confirmed"}</RuleResult>;
            }
            return (
              <tr key={k} data-testid={`${testId}-${k.replace(/[^a-zA-Z]/g, "")}`}>
                <td style={{ color: "var(--text-primary)" }}>{k}</td>
                <td className="num">{g.n}</td>
                <td className="num">{pct0(g.hit_rate)}</td>
                <td className="num"><Pnl value={g.expectancy} /></td>
                <td className="num"><Pnl value={g.total_pnl} dp={0} /></td>
                <td>{verdict}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SubTable>
  );
}

export function RegimeExplorerCard() {
  const [run, setRun] = useState(3);
  const { data, isFetching, isError } = useQuery({ queryKey: ["bt-regimes", run], queryFn: () => api.btRegimes(run), retry: false });
  return (
    <Panel title="Regime explorer · when to trade vs skip" icon={CalendarRange} data-testid="bt-regimes"
           actions={<RunSelect value={run} onChange={setRun} testId="bt-regimes-run" label={null} />}>
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          Slices the stored backtest by day of week, implied-vol tercile and opening gap. TRADE is shown only for a slice with at least {MIN_N} trades, a positive average and a lower 95% confidence bound above zero.
          &quot;Mean &gt; 0, not yet confirmed&quot; means the average is positive but the sample does not yet prove it. Smaller slices read n &lt; {MIN_N}.
        </p>
        {isError && <p className="sn-muted" style={{ margin: 0 }} data-testid="bt-regimes-empty">No stored backtest for this entry checkpoint yet. Run Compare intervals or a single backtest first, then reopen.</p>}
        {isFetching && <Skeleton lines={3} />}
        {data && !isError && (
          <>
            <span className="sn-small sn-faint"><span className="sn-num">{data.start} → {data.end}</span> · overall <span className="sn-num">{usd(data.overall?.expectancy)}</span>/trade over <span className="sn-num">{data.overall?.n}</span> trades</span>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <RegimeTable title="By day of week" groups={data.by_dow} testId="bt-regime-dow" />
              <RegimeTable title="By implied-vol tercile" groups={data.by_vol} testId="bt-regime-vol" />
              <RegimeTable title="By opening gap" groups={data.by_gap} testId="bt-regime-gap" />
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

// -------------------------------------------------------------- Position sizing
function SizeStat({ label, value, sub }) {
  return (
    <div className="sn-stat sn-stat-md">
      <span className="sn-stat-k">{label}</span>
      <span className="sn-stat-v">{value}</span>
      {sub && <span className="sn-stat-sub sn-sans-num">{sub}</span>}
    </div>
  );
}

export function SizingCard() {
  const [run, setRun] = useState(3);
  const [bankroll, setBankroll] = useState(100000);
  const [kelly, setKelly] = useState(0.25);
  const { data, isFetching, isError } = useQuery({
    queryKey: ["bt-sizing", run, bankroll, kelly],
    queryFn: () => api.btSizing(run, Number(bankroll), Number(kelly)),
    retry: false,
  });
  return (
    <Panel title="Position sizing · fractional Kelly on calibrated edge" icon={Scale} data-testid="bt-sizing"
           actions={<RunSelect value={run} onChange={setRun} testId="bt-sizing-run" label={null} />}>
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          Uses the <b style={{ color: "var(--text-primary)" }}>realized</b> (calibrated) win rate, not the model&apos;s own probability, and the realized payoff ratio to suggest a fractional-Kelly size. It recommends a size only; Sentinel never places orders.
        </p>
        <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
          <NumInput label="Bankroll ($)" value={bankroll} onChange={setBankroll} step="1000" width={144} testId="bt-sizing-bankroll" />
          <NumInput label="Kelly fraction" value={kelly} onChange={setKelly} step="0.05" testId="bt-sizing-kelly" />
        </div>
        {isError && <p className="sn-muted" style={{ margin: 0 }} data-testid="bt-sizing-empty">No stored backtest for this entry checkpoint yet. Run a backtest first, then reopen.</p>}
        {isFetching && <Skeleton lines={2} />}
        {data?.insufficient && (
          <p className="sn-row sn-small" style={{ margin: 0, color: "var(--text-primary)" }} data-testid="bt-sizing-insufficient">
            <RuleResult result="UNCONFIRMED">n &lt; 5</RuleResult>
            Only {data.n} trades stored for this checkpoint; a sizing estimate needs at least 5. Run a longer backtest.
          </p>
        )}
        {data && !data.insufficient && !isError && smallSample(data.n) && <SmallSampleNote n={data.n} what="a position size" data-testid="bt-sizing-small" />}
        {data && !data.insufficient && !isError && (
          <>
            {/* The stat tiles are hidden below MIN_N trades, so no "Suggested risk $X" sits above a "no size" verdict. */}
            {!smallSample(data.n) && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="bt-sizing-metrics">
                <SizeStat label="Calibrated win rate" value={`${(data.win_prob_calibrated * 100).toFixed(1)}%`} sub={`model said ${(data.win_prob_model * 100).toFixed(1)}%`} />
                <SizeStat label="Payoff ratio (b)" value={data.payoff_ratio_b ?? "—"} sub={`avg win ${usd(data.avg_win)} / loss ${usd(data.avg_loss)}`} />
                <SizeStat label="Full Kelly f*" value={`${(data.kelly_full * 100).toFixed(1)}%`} sub={`× ${data.kelly_fraction} fraction`} />
                <SizeStat label="Suggested risk" value={usd(data.risk_dollars, 0)} sub={`${(data.recommended_fraction * 100).toFixed(2)}% of bankroll`} />
              </div>
            )}
            <p className="sn-small" style={{ margin: 0, padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)" }}
               data-testid="bt-sizing-verdict">
              {smallSample(data.n) ? (
                <>No size is suggested from {data.n} trades ({data.start} → {data.end}). The sizing figures stay hidden until the sample reaches {MIN_N} trades.</>
              ) : data.edge_positive ? (
                <>At {(data.win_prob_calibrated * 100).toFixed(1)}% calibrated hit rate and {data.payoff_ratio_b}× payoff, a {data.kelly_fraction}-Kelly stake is
                  {" "}<b className="sn-num" style={{ color: "var(--text-primary)" }}>{usd(data.risk_dollars, 0)}</b>, about <b style={{ color: "var(--text-primary)" }}>{data.suggested_contracts} spread{data.suggested_contracts === 1 ? "" : "s"}</b> at {usd(data.avg_max_loss_per_spread)} max loss each. {data.start} → {data.end}, n={data.n}.</>
              ) : (
                <span style={{ color: "var(--text-primary)" }}>No positive Kelly edge at the calibrated win rate, so the suggested size is 0. The edge does not clear costs for this checkpoint over {data.start} → {data.end}.</span>
              )}
            </p>
          </>
        )}
      </div>
    </Panel>
  );
}
